// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import * as Sentry from "@sentry/browser";
import "codemirror/lib/codemirror.css";
import isFunction from "lodash/isFunction";
import App from "next/app";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import { ReactNode, Suspense, useEffect, useMemo, useState } from "react";
import { I18nProvider } from "react-aria";
import { RouterProvider } from "react-aria-components";
import { I18nextProvider, Translation, useTranslation } from "react-i18next";
import Validators from "redux-form-validators";
import { AppThemeProvider } from "src/components/shared/AppThemeProvider";
import { useCookiePreferencesStore, CookiePolicyGuard } from "src/features/cookie-policy";

import { RBACProvider, RouteAccessGuard } from "@sol/access-control";
import { AuthenticatedUserProvider } from "@sol/authentication/AuthenticatedUserContext";
import { authorizationInterceptor, refreshTokenInterceptor } from "@sol/authentication/interceptors";
import { useAuthenticationStore } from "@sol/authentication/useAuthenticationStore";
import { StorageProvider, useStorage } from "@sol/persistance";
import { NavigationProvider } from "@sol/routing/useNavigation";
import { SDKProvider } from "@sol/sdk";
import { Skeleton } from "@sol/uikit/feedback/Skeleton/Skeleton";

import { analytics, AnalyticsProvider, grantAnalyticsConsent, revokeAnalyticsConsent } from "@/core/analytics";
import useLocale from "@/shared/hooks/useLocale";

import HttpErrorBoundary from "../components/HttpErrorBoundary";
import { AppLayout } from "../components/layouts";
import NoSSR from "../components/NoSSR";
import i18n, { localeInterceptor } from "../i18n";
import { maintenanceInterceptor } from "../interceptors";

import "../modules/uikit/theme/styles/global.css";

Sentry.init({
    dsn: process.env.SENTRY_DSN,
    environment: process.env.ENV,
    release: process.env.VERSION,
    // Browser extensions (crypto wallets, etc.) inject scripts that throw in the page context
    denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//, /^safari-(web-)?extension:\/\//, /^file:\/\//],
    ignoreErrors: ["Cannot redefine property: ethereum", "Cannot redefine property: solana"],
    // Pages saved to disk and reopened over file:// run outside their origin and cannot work:
    // denyUrls relies on stack frame urls, which unhandled rejections do not always carry
    beforeSend: event => (typeof window !== "undefined" && !window.location.protocol.startsWith("http") ? null : event),
});

// Setup redux-form-validators i18n bindings

Validators.formatMessage = msg => <Translation>{t => t(msg.id, msg.values)}</Translation>;

type TokenConsumerValue = { token?: string; userId?: string };

const TokenConsumer = ({ children }: { children: (value: TokenConsumerValue) => ReactNode }) => {
    const { token, userId } = useAuthenticationStore();

    return children({ token: token ?? undefined, userId: userId ?? undefined });
};

const getStorage = () => {
    if (typeof window !== "undefined") {
        return new Proxy(window.localStorage, {
            get: (storage: Storage, prop: string) => {
                if (prop === "setItem") {
                    return (key: string, value: string) => storage.setItem(`sol:${key}`, value);
                } else if (prop === "getItem") {
                    return (key: string) => storage.getItem(`sol:${key}`);
                } else if (prop === "removeItem") {
                    return (key: string) => storage.removeItem(`sol:${key}`);
                }

                return storage[prop];
            },
        });
    }
    return undefined;
};

const ReactQueryDevtools = dynamic(() => import("@tanstack/react-query-devtools").then(mod => mod.ReactQueryDevtools));

const Devtools = () => {
    const storage = useStorage();
    const [activated, setActivated] = useState(false);
    const [t] = useTranslation();

    const devtoolsActivated = useMemo(() => {
        return activated || storage.getItem("devtools");
    }, [storage, activated]);

    useEffect(() => {
        if (window) {
            window.devtools = enable => setActivated(!!enable);

            return () => {
                delete window.devtools;
            };
        }
    }, [setActivated]);

    useEffect(() => {
        if (window?.devtools && devtoolsActivated) {
            window.devtools.translate = t;

            return () => {
                if (window?.devtools) {
                    delete window.devtools.translate;
                }
            };
        }
    }, [devtoolsActivated, t]);

    if (devtoolsActivated) {
        return <ReactQueryDevtools />;
    }

    return null;
};

const AriaI18nProvider = ({
    children,
}: {
    children: ReactNode | ((ctx: { locale: string; i18n: typeof i18n }) => ReactNode);
}) => {
    const [locale] = useLocale();

    return (
        <I18nextProvider i18n={i18n}>
            <I18nProvider locale={locale}>{isFunction(children) ? children({ locale, i18n }) : children}</I18nProvider>
        </I18nextProvider>
    );
};

function updateAnalytics({ isPolicyAccepted, cookies }: ReturnType<typeof useCookiePreferencesStore.getState>) {
    if (isPolicyAccepted && cookies.analytics.value && !cookies.analytics.disabled) {
        grantAnalyticsConsent();
    } else {
        revokeAnalyticsConsent();
    }
}

updateAnalytics(useCookiePreferencesStore.getState());
useCookiePreferencesStore.subscribe(updateAnalytics);

useAuthenticationStore.subscribe((state, prevState) => {
    if (state.token && !prevState.token) {
        // the user is now authenticated
        const { userId } = state;
        const { roles = [] } = state.tokenInfo() ?? {};

        const role = (roles.length ?? 0) > 0 ? roles[0] : undefined;

        if (userId && role) {
            analytics.identify({
                userId,
                traits: {
                    role,
                },
            });
        } else {
            // Log to the console as this is likely a dev issue but not a fatal one
            // eslint-disable-next-line no-console
            console.error("[SOL:Analytics]: can't identify user, missing userId or role", { userId, role });
        }
    } else if (!state.token && prevState.token) {
        // the user is now anonymous
        analytics.identify(undefined);
    }
});

const sdkInterceptors = [authorizationInterceptor, refreshTokenInterceptor, localeInterceptor, maintenanceInterceptor];

class MyApp extends App {
    componentDidCatch(error: Error, errorInfo: any) {
        Sentry.withScope(scope => {
            Object.keys(errorInfo).forEach(key => {
                scope.setExtra(key, errorInfo[key]);
            });

            Sentry.captureException(error);
        });
    }

    getNextRouter() {
        return useRouter();
    }

    render() {
        const { Component, pageProps } = this.props;

        const getLayout = Component.getLayout ?? (page => page);

        return (
            <>
                <Head>
                    <title>Simplonline</title>
                </Head>
                <NoSSR>
                    {/** DEBT there must be a way to factorize this code below. I see too much depth */}
                    <AppThemeProvider>
                        <Suspense fallback={<Skeleton.Node className="size-full!" active />}>
                            <AriaI18nProvider>
                                <CookiePolicyGuard>
                                    <AnalyticsProvider>
                                        <StorageProvider storage={getStorage()}>
                                            <HttpErrorBoundary>
                                                <TokenConsumer>
                                                    {({ userId }) => {
                                                        const isAuthenticated = !!userId;
                                                        return (
                                                            <SDKProvider
                                                                baseURL={process.env.API_URL ?? ""}
                                                                interceptors={sdkInterceptors}
                                                            >
                                                                <AuthenticatedUserProvider userId={userId}>
                                                                    {({ user, settings }) => {
                                                                        if (isAuthenticated && !(user && settings)) {
                                                                            return null;
                                                                        }

                                                                        const role =
                                                                            (user?.roles?.length ?? 0) > 0
                                                                                ? user.roles[0]
                                                                                : undefined;

                                                                        return (
                                                                            <RBACProvider role={role}>
                                                                                <RouteAccessGuard>
                                                                                    <RouterProvider
                                                                                        navigate={
                                                                                            this.getNextRouter().push
                                                                                        }
                                                                                    >
                                                                                        <NavigationProvider>
                                                                                            <AppLayout {...pageProps}>
                                                                                                {getLayout(
                                                                                                    <Component
                                                                                                        {...pageProps}
                                                                                                    />,
                                                                                                )}
                                                                                            </AppLayout>
                                                                                        </NavigationProvider>
                                                                                    </RouterProvider>

                                                                                    <Devtools />
                                                                                </RouteAccessGuard>
                                                                            </RBACProvider>
                                                                        );
                                                                    }}
                                                                </AuthenticatedUserProvider>
                                                            </SDKProvider>
                                                        );
                                                    }}
                                                </TokenConsumer>
                                            </HttpErrorBoundary>
                                        </StorageProvider>
                                    </AnalyticsProvider>
                                </CookiePolicyGuard>
                            </AriaI18nProvider>
                        </Suspense>
                    </AppThemeProvider>
                </NoSSR>
            </>
        );
    }
}

MyApp.displayName = "MyApp";

export default MyApp;
